import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PartnerMarketingKit } from "@/components/partners/PartnerMarketingKit";
import { PartnerLeaderboard } from "@/components/partners/PartnerLeaderboard";
import { toast } from "sonner";
import { Loader2, Share2, Copy, Wallet, Users, TrendingUp, Sparkles, Crown, Check, Clock } from "lucide-react";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;

export default function Partners() {
  const [data, setData] = useState(null);
  const [joining, setJoining] = useState("");
  const [amount, setAmount] = useState("");
  const [details, setDetails] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  const load = () => api.get("/partners/me").then((r) => setData(r.data)).catch(() => setData({ partner: null }));
  useEffect(() => { load(); }, []);

  const join = async (type) => {
    setJoining(type);
    try { await api.post("/partners/join", { type }); toast.success(type === "affiliate" ? "You're an affiliate — start sharing!" : "Application submitted — awaiting approval"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setJoining("");
  };

  const withdraw = async () => {
    setWithdrawing(true);
    try {
      await api.post("/partners/withdraw", { amount: parseFloat(amount), details });
      toast.success("Payout requested");
      setAmount(""); setDetails(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setWithdrawing(false);
  };

  if (!data) return <Layout><div className="py-24 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div></Layout>;

  const p = data.partner;
  const s = data.settings || {};
  const refLink = p ? `${window.location.origin}/?ref=${p.ref_code}` : "";

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Share2 className="w-5 h-5" /></span>
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl">Earn with Xurray</h1>
            <p className="text-sm text-muted-foreground">Share products, grow the community, and get paid.</p>
          </div>
        </div>

        {!p ? (
          <div className="grid md:grid-cols-2 gap-5 mt-6">
            <Card className="p-6 flex flex-col" data-testid="join-affiliate-card">
              <Sparkles className="w-7 h-7 text-primary mb-2" />
              <h2 className="font-bold text-lg">Affiliate</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-4">Join instantly and start earning right away — perfect for anyone who wants to share Xurray.</p>
              <ul className="text-sm space-y-1.5 mb-5">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />{s.affiliate_sales_pct}% commission on referred sales</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />{money(s.affiliate_signup_reward)} per new sign-up</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />Instant activation</li>
              </ul>
              <Button className="mt-auto rounded-full" data-testid="join-affiliate-btn" disabled={joining} onClick={() => join("affiliate")}>
                {joining === "affiliate" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join as Affiliate"}
              </Button>
            </Card>
            <Card className="p-6 flex flex-col border-primary/40 ring-1 ring-primary/10" data-testid="join-influencer-card">
              <Crown className="w-7 h-7 text-primary mb-2" />
              <h2 className="font-bold text-lg">Influencer <Badge className="ml-1 bg-primary">Higher pay</Badge></h2>
              <p className="text-sm text-muted-foreground mt-1 mb-4">For creators with an audience. Apply for review and unlock premium commission rates.</p>
              <ul className="text-sm space-y-1.5 mb-5">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />{s.influencer_sales_pct}% commission on referred sales</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />{money(s.influencer_signup_reward)} per new sign-up</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />Admin-approved status</li>
              </ul>
              <Button variant="outline" className="mt-auto rounded-full border-primary/50" data-testid="join-influencer-btn" disabled={joining} onClick={() => join("influencer")}>
                {joining === "influencer" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply as Influencer"}
              </Button>
            </Card>
            <div className="md:col-span-2"><PartnerLeaderboard compact /></div>
          </div>
        ) : (
          <div className="space-y-5 mt-6">
            {p.status === "pending" && (
              <Card className="p-4 flex items-center gap-3 bg-amber-50 border-amber-200" data-testid="partner-pending">
                <Clock className="w-5 h-5 text-amber-600" />
                <p className="text-sm">Your <strong>Influencer</strong> application is under review. You'll be notified once approved.</p>
              </Card>
            )}
            {p.status === "rejected" && (
              <Card className="p-4 text-sm bg-red-50 border-red-200" data-testid="partner-rejected">Your influencer application was not approved. Contact support if you think this is a mistake.</Card>
            )}

            <Tabs defaultValue="overview" className="w-full">
              <TabsList data-testid="partner-tabs">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                {p.status === "active" && <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing Kit</TabsTrigger>}
                <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaderboard</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-5 mt-5">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { icon: Wallet, label: "Available balance", value: money(p.wallet_balance), test: "stat-balance" },
                    { icon: TrendingUp, label: "Total earned", value: money(p.earnings), test: "stat-earnings" },
                    { icon: Users, label: "Sign-ups", value: p.signups, test: "stat-signups" },
                    { icon: TrendingUp, label: "Referred sales", value: p.sales_count, test: "stat-sales" },
                  ].map((x) => (
                    <Card key={x.label} className="p-4" data-testid={x.test}>
                      <x.icon className="w-4 h-4 text-primary mb-2" />
                      <p className="text-xl font-black font-display">{x.value}</p>
                      <p className="text-xs text-muted-foreground">{x.label}</p>
                    </Card>
                  ))}
                </div>

                {/* Ref link */}
                <Card className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Your referral link</h3>
                    <Badge variant="outline" className="capitalize">{p.type} · {p.effective_sales_pct}% + {money(p.effective_signup_reward)}/signup</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Input readOnly value={refLink} data-testid="ref-link-input" className="font-mono text-xs" />
                    <Button data-testid="copy-ref-btn" onClick={() => { navigator.clipboard.writeText(refLink); toast.success("Link copied"); }}><Copy className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Share this link. You earn when people sign up or buy through it.</p>
                </Card>

                {/* Withdraw */}
                {p.status === "active" && (
                  <Card className="p-5">
                    <h3 className="font-semibold mb-1">Request a payout</h3>
                    <p className="text-xs text-muted-foreground mb-3">Minimum {money(data.min_withdraw)}. Available: <strong>{money(p.wallet_balance)}</strong>.</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input type="number" placeholder="Amount (€)" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount" className="sm:w-40" />
                      <Input placeholder="Payout details (bank / PayPal email)" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="withdraw-details" className="flex-1" />
                      <Button data-testid="withdraw-btn" disabled={withdrawing || !amount} onClick={withdraw}>{withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request"}</Button>
                    </div>
                  </Card>
                )}

                {/* Ledger */}
                <Card className="p-5">
                  <h3 className="font-semibold mb-3">Earnings history</h3>
                  {(!data.ledger || data.ledger.length === 0) ? (
                    <p className="text-sm text-muted-foreground">No earnings yet — share your link to get started.</p>
                  ) : (
                    <div className="divide-y">
                      {data.ledger.map((l) => (
                        <div key={l.ledger_id} className="flex items-center justify-between py-2 text-sm" data-testid={`ledger-${l.ledger_id}`}>
                          <div>
                            <span className="font-medium capitalize">{l.type}</span>
                            <span className="text-muted-foreground"> · {l.note}</span>
                            <div className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</div>
                          </div>
                          <span className="font-bold text-green-600">+{money(l.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              {p.status === "active" && (
                <TabsContent value="marketing" className="mt-5">
                  <PartnerMarketingKit />
                </TabsContent>
              )}

              <TabsContent value="leaderboard" className="mt-5">
                <PartnerLeaderboard />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </Layout>
  );
}
